// ORKSTRA Design Tokens v2
// Single source of truth for colors, typography, spacing, shadows
// Supports Light + Dark themes with brand consistency
//
// Used by:
//   - Theme Provider (Agent E)
//   - All nav components (Agents A, B, C, D)
//   - Future design system components

export const tokens = {
  colors: {
    light: {
      bg: {
        primary: '#FFFFFF',
        secondary: '#F8FAFC',
        tertiary: '#F1F5F9',
        sidebar: '#0F172A',        // Dark sidebar in light mode (Linear-style)
        topbar: '#FFFFFF',
        card: '#FFFFFF',
      },
      text: {
        primary: '#0F172A',
        secondary: '#475569',
        tertiary: '#94A3B8',
        inverse: '#FFFFFF',
        sidebar: '#F8FAFC',
      },
      border: {
        DEFAULT: '#E2E8F0',
        strong: '#CBD5E1',
        subtle: '#F1F5F9',
      },
      brand: {
        primary: '#1E40AF',        // ORKSTRA Blue
        primaryHover: '#1E3A8A',
        secondary: '#D4A856',       // Gold
        accent: '#3B82F6',
      },
      semantic: {
        success: '#10B981',
        warning: '#F59E0B',
        danger: '#EF4444',
        info: '#3B82F6',
      },
    },
    dark: {
      bg: {
        primary: '#0F172A',
        secondary: '#1E293B',
        tertiary: '#334155',
        sidebar: '#020617',         // Even darker sidebar
        topbar: '#0F172A',
        card: '#1E293B',
      },
      text: {
        primary: '#FFFFFF',         // Pure white for max contrast on dark bg
        secondary: '#E2E8F0',       // Brighter gray for secondary text
        tertiary: '#94A3B8',        // Bumped up from #64748B for readability
        inverse: '#0F172A',
        sidebar: '#F1F5F9',         // Brighter sidebar text
      },
      border: {
        DEFAULT: '#334155',         // Slightly more visible
        strong: '#64748B',          // Bumped up for visibility
        subtle: '#1E293B',
      },
      brand: {
        primary: '#3B82F6',         // Lighter blue in dark mode
        primaryHover: '#60A5FA',
        secondary: '#FCD34D',
        accent: '#60A5FA',
      },
      semantic: {
        success: '#10B981',
        warning: '#FBBF24',
        danger: '#F87171',
        info: '#60A5FA',
      },
    },
  },

  typography: {
    fontFamilyEN: 'Inter, system-ui, sans-serif',
    fontFamilyAR: 'Cairo, Tajawal, sans-serif',
    sizes: {
      xs: '0.75rem',     // 12px
      sm: '0.875rem',    // 14px
      base: '1rem',      // 16px
      lg: '1.125rem',    // 18px
      xl: '1.25rem',     // 20px
      '2xl': '1.5rem',   // 24px
      '3xl': '1.875rem', // 30px
    },
    weights: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
  },

  spacing: {
    xs: '0.25rem',  // 4px
    sm: '0.5rem',   // 8px
    md: '1rem',     // 16px
    lg: '1.5rem',   // 24px
    xl: '2rem',     // 32px
    '2xl': '3rem',  // 48px
  },

  radii: {
    none: '0',
    sm: '0.25rem',
    md: '0.5rem',
    lg: '0.75rem',
    xl: '1rem',
    full: '9999px',
  },

  shadows: {
    light: {
      sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
      md: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
      lg: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
      glow: '0 0 0 3px rgb(30 64 175 / 0.15)',
    },
    dark: {
      sm: '0 1px 2px 0 rgb(0 0 0 / 0.5)',
      md: '0 4px 6px -1px rgb(0 0 0 / 0.4)',
      lg: '0 10px 15px -3px rgb(0 0 0 / 0.5)',
      glow: '0 0 0 3px rgb(59 130 246 / 0.3)',
    },
  },

  transitions: {
    fast: '150ms cubic-bezier(0.4, 0, 0.2, 1)',
    normal: '250ms cubic-bezier(0.4, 0, 0.2, 1)',
    slow: '400ms cubic-bezier(0.4, 0, 0.2, 1)',
  },

  zIndex: {
    base: 0,
    dropdown: 10,
    sticky: 20,
    sidebar: 30,
    topbar: 40,
    modal: 50,
    popover: 60,
    tooltip: 70,
    commandPalette: 80,
  },
} as const;

export type Tokens = typeof tokens;
export type ColorMode = 'light' | 'dark';
export type ThemePreference = 'light' | 'dark' | 'auto';
